import { useState } from 'react';
import {
    Box,
    Button,
    Collapse,
    Divider,
    Fab,
    FormControlLabel,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

const primaryDialogButtonSx = {
    minHeight: 48,
    px: 2.2,
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: '16px',
    bgcolor: '#3D3D3D',
    boxShadow: 'none',
    '&:hover': {
        bgcolor: '#3D3D3D',
        boxShadow: 'none'
    }
};

const secondaryDialogButtonSx = {
    minHeight: 48,
    px: 2.2,
    textTransform: 'none',
    fontWeight: 700,
    color: '#3D3D3D',
    borderColor: '#D9D9D9',
    borderWidth: '2px',
    borderRadius: '16px',
    '&:hover': {
        bgcolor: '#F7F7F7',
        borderColor: '#CFCFCF',
        borderWidth: '2px'
    }
};

const createReadOnlyViewFieldSx = (isReadOnly) => ({
    ...(isReadOnly
        ? {
              '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: '#6F6F6F',
                  color: '#6F6F6F'
              },
              '& .MuiInputBase-input.Mui-disabled::placeholder': {
                  WebkitTextFillColor: '#8A8A8A'
              },
              '& .MuiSelect-select.Mui-disabled': {
                  WebkitTextFillColor: '#6F6F6F',
                  color: '#6F6F6F'
              }
          }
        : {})
});

const toPickerValue = (value) => {
    if (!value) {
        return null;
    }

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
};

const CaregiverPersonalDataSection = ({
    form,
    readOnly,
    loading,
    submitting,
    onChange,
    onEdit,
    onSave,
    onCancel,
    hasPatient,
    patientForm,
    patientReadOnly,
    patientSubmitting,
    showPatientForm,
    onAddPatientClick,
    onPatientChange,
    onPatientEdit,
    onPatientSave,
    onPatientCancel,
    onReferenceContactChange,
    onAddReferenceContact,
    onRemoveReferenceContact,
    onEquipmentItemChange,
    onAddEquipmentItem,
    onRemoveEquipmentItem,
    onDeleteCaregiver,
    onDeletePatient,
    canDeleteCaregiver,
    canDeletePatient,
    deletingCaregiver,
    deletingPatient,
    isSubordinate,
    subordinateUsername,
    onResetSubordinatePassword,
    locationOptions = []
}) => {
    const [caregiverExpanded, setCaregiverExpanded] = useState(false);
    const [patientExpanded, setPatientExpanded] = useState(false);

    const stopPropagation = (event) => {
        event.stopPropagation();
    };

    const handleCaregiverHeaderClick = () => {
        if (!caregiverExpanded) {
            setCaregiverExpanded(true);
        }
    };

    const handlePatientHeaderClick = () => {
        if (!hasPatient && !showPatientForm) {
            onAddPatientClick();
            setPatientExpanded(true);
            return;
        }

        if (!patientExpanded) {
            setPatientExpanded(true);
        }
    };

    return (
        <Box>
            <Box
                sx={{
                    p: 3,
                    borderRadius: 2,
                    border: 1,
                    borderColor: '#dbdbdb',
                    bgcolor: '#ffffff',
                    mb: 3
                }}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    onClick={handleCaregiverHeaderClick}
                    sx={{ cursor: caregiverExpanded ? 'default' : 'pointer' }}
                >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                        <Typography variant="h6" fontWeight={700} mb={2}>
                            Ficha Pessoal: Cuidador
                        </Typography>
                        {caregiverExpanded && readOnly ? (
                            <Stack direction="row" spacing={1}>
                                {canDeleteCaregiver ? (
                                    <Tooltip title="Eliminar cuidador">
                                        <span>
                                            <Fab
                                                size="small"
                                                sx={{ bgcolor: '#3D3D3D', color: '#fff', boxShadow: 'none' }}
                                                onClick={(event) => {
                                                    stopPropagation(event);
                                                    onDeleteCaregiver();
                                                }}
                                                disabled={loading || deletingCaregiver}
                                            >
                                                <DeleteOutlineRoundedIcon />
                                            </Fab>
                                        </span>
                                    </Tooltip>
                                ) : null}
                                <Tooltip title="Editar dados do cuidador">
                                    <span>
                                        <Fab
                                            size="small"
                                            sx={{ bgcolor: '#D63731', color: '#fff', boxShadow: 'none' }}
                                            onClick={(event) => {
                                                stopPropagation(event);
                                                onEdit();
                                            }}
                                            disabled={loading}
                                        >
                                            <Box
                                                component="img"
                                                src="/assets/logos-svg/edit.svg"
                                                alt="Editar"
                                                sx={{ width: 18, height: 18, display: 'block' }}
                                            />
                                        </Fab>
                                    </span>
                                </Tooltip>
                            </Stack>
                        ) : null}
                    </Stack>
                    <Tooltip title={caregiverExpanded ? 'Fechar ficha do cuidador' : 'Abrir ficha do cuidador'}>
                        <IconButton
                            onClick={(event) => {
                                stopPropagation(event);
                                setCaregiverExpanded((prev) => !prev);
                            }}
                        >
                            <ExpandMoreIcon
                                sx={{
                                    transform: caregiverExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform .2s ease'
                                }}
                            />
                        </IconButton>
                    </Tooltip>
                </Stack>

                <Collapse in={caregiverExpanded}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                            gap: 2,
                            mb: 2,
                            mt: 2,
                            ...createReadOnlyViewFieldSx(readOnly)
                        }}
                    >
                        <TextField
                            label="Nome Completo"
                            fullWidth
                            value={form.name}
                            onChange={onChange('name')}
                            disabled={readOnly || submitting}
                        />
                        <TextField
                            label="Género"
                            fullWidth
                            select
                            value={form.gender}
                            onChange={onChange('gender')}
                            disabled={readOnly || submitting}
                        >
                            <MenuItem value="">Selecionar</MenuItem>
                            <MenuItem value="WOMAN">Feminino</MenuItem>
                            <MenuItem value="MAN">Masculino</MenuItem>
                            <MenuItem value="NOT_LISTED">Outro</MenuItem>
                        </TextField>
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                            gap: 2,
                            mb: 2
                        }}
                    >
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Data de Nascimento"
                                format="DD/MM/YYYY"
                                value={toPickerValue(form.birthDate)}
                                onChange={(newValue) =>
                                    onChange('birthDate')({
                                        target: {
                                            value:
                                                newValue && dayjs(newValue).isValid()
                                                    ? dayjs(newValue).format('YYYY-MM-DD')
                                                    : ''
                                        }
                                    })
                                }
                                disabled={readOnly || submitting}
                                slotProps={{
                                    textField: {
                                        fullWidth: true
                                    }
                                }}
                            />
                        </LocalizationProvider>
                        <TextField
                            label="Idade"
                            fullWidth
                            value={form.age}
                            disabled
                            InputProps={{ readOnly: true }}
                        />
                        <TextField
                            label="Contato Telefónico"
                            fullWidth
                            value={form.phone}
                            onChange={onChange('phone')}
                            disabled={readOnly || submitting}
                        />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <TextField
                            label="E-mail"
                            fullWidth
                            value={form.email || ''}
                            disabled
                            InputProps={{ readOnly: true }}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                            gap: 2,
                            mb: 2
                        }}
                    >
                        <TextField
                            label="Morada"
                            fullWidth
                            value={form.street}
                            onChange={onChange('street')}
                            disabled={readOnly || submitting}
                        />
                        <TextField
                            label="Código Postal"
                            fullWidth
                            value={form.zip}
                            onChange={onChange('zip')}
                            disabled={readOnly || submitting}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                            gap: 2,
                            mb: 2
                        }}
                    >
                        <TextField
                            label="Freguesia"
                            fullWidth
                            value={form.parish}
                            onChange={onChange('parish')}
                            disabled={readOnly || submitting}
                        />
                        <TextField
                            label="Concelho"
                            fullWidth
                            value={form.municipality}
                            onChange={onChange('municipality')}
                            disabled={readOnly || submitting}
                        />
                        <TextField
                            label="Localização SOS"
                            fullWidth
                            select
                            value={form.locationUuid || ''}
                            onChange={onChange('locationUuid')}
                            disabled={readOnly || submitting}
                            helperText="Localização usada para determinar os contactos SOS do cuidador."
                        >
                            <MenuItem value="">Sem localização</MenuItem>
                            {locationOptions.map((option) => (
                                <MenuItem key={option.uuid} value={option.uuid}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                            gap: 2,
                            mb: 2
                        }}
                    >
                        <TextField
                            label="N.º BI / CC"
                            fullWidth
                            value={form.idCard}
                            onChange={onChange('idCard')}
                            disabled={readOnly || submitting}
                        />
                        <TextField
                            label="NIF"
                            fullWidth
                            value={form.nif}
                            onChange={onChange('nif')}
                            disabled={readOnly || submitting}
                        />
                        <TextField
                            label="N.º Utente"
                            fullWidth
                            value={form.healthNumber}
                            onChange={onChange('healthNumber')}
                            disabled={readOnly || submitting}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                            gap: 2,
                            mb: 2
                        }}
                    >
                        <TextField
                            label="NISS"
                            fullWidth
                            value={form.niss}
                            onChange={onChange('niss')}
                            disabled={readOnly || submitting}
                        />
                        <Box />
                        <Box />
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                            gap: 2,
                            mb: 3
                        }}
                    >
                        <TextField
                            label="Estado Civil"
                            fullWidth
                            value={form.maritalStatus}
                            onChange={onChange('maritalStatus')}
                            disabled={readOnly || submitting}
                        />
                        <TextField
                            label="Escolaridade"
                            fullWidth
                            value={form.education}
                            onChange={onChange('education')}
                            disabled={readOnly || submitting}
                        />
                        <TextField
                            label="Situação Profissional"
                            fullWidth
                            value={form.professionalStatus}
                            onChange={onChange('professionalStatus')}
                            disabled={readOnly || submitting}
                        />
                    </Box>

                    {isSubordinate ? (
                        <Box>
                            <Typography variant="h6" fontWeight={700} mb={1}>
                                Conta e email
                            </Typography>
                            <Box
                                sx={{
                                    p: 2,
                                    mb: 3,
                                    borderRadius: 2,
                                    border: '1px solid #E2E2E2',
                                    bgcolor: '#FAFAFA'
                                }}
                            >
                                <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    justifyContent="space-between"
                                    alignItems={{ xs: 'stretch', md: 'center' }}
                                    spacing={2}
                                    mb={2}
                                >
                                    <Box>
                                        <Typography variant="body2" color="#666666">
                                            Este cuidador autentica-se com username e password proprios
                                            apos o login principal.
                                        </Typography>
                                    </Box>
                                    <Tooltip title="Repor password da conta partilhada">
                                        <Button
                                            variant="outlined"
                                            onClick={onResetSubordinatePassword}
                                            sx={secondaryDialogButtonSx}
                                        >
                                            Repor Password
                                        </Button>
                                    </Tooltip>
                                </Stack>

                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr',
                                        gap: 2
                                    }}
                                >
                                    <TextField
                                        label="Username"
                                        fullWidth
                                        value={subordinateUsername || ''}
                                        disabled
                                        InputProps={{ readOnly: true }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    ) : null}
                    

                    <Typography variant="h6" fontWeight={700} mb={1}>
                        Situação Familiar
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                            gap: 2,
                            mb: 3
                        }}
                    >
                        <TextField
                            label="Relação de parentesco com a pessoa dependente"
                            fullWidth
                            value={form.kinshipToDependent}
                            onChange={onChange('kinshipToDependent')}
                            disabled={readOnly || submitting}
                        />
                        <TextField
                            label="Vive em situaação de isolamento?"
                            fullWidth
                            value={form.livesInIsolation}
                            onChange={onChange('livesInIsolation')}
                            disabled={readOnly || submitting}
                        />
                    </Box>

                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6" fontWeight={700}>
                            Outros familiares, vizinhos e/ou outras pessoas de referencia
                        </Typography>
                        <Tooltip title="Adicionar contacto de referência">
                            <span>
                                <IconButton
                                    onClick={onAddReferenceContact}
                                    disabled={readOnly || submitting}
                                    sx={{ color: '#D63731' }}
                                >
                                    <AddCircleOutlineRoundedIcon />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                    <Stack spacing={2} mb={3}>
                        {(form.referenceContacts || []).map((contact, index) => (
                            <Box
                                key={`reference-contact-${index}`}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '2fr 2fr 0.7fr 2fr auto' },
                                    gap: 2,
                                    alignItems: 'center'
                                }}
                            >
                                <TextField
                                    label="Nome"
                                    fullWidth
                                    value={contact.name}
                                    onChange={onReferenceContactChange(index, 'name')}
                                    disabled={readOnly || submitting}
                                />
                                <TextField
                                    label="Parentesco"
                                    fullWidth
                                    value={contact.kinship}
                                    onChange={onReferenceContactChange(index, 'kinship')}
                                    disabled={readOnly || submitting}
                                />
                                <TextField
                                    label="Idade"
                                    fullWidth
                                    value={contact.age}
                                    onChange={onReferenceContactChange(index, 'age')}
                                    disabled={readOnly || submitting}
                                />
                                <TextField
                                    label="Telefone"
                                    fullWidth
                                    value={contact.phone}
                                    onChange={onReferenceContactChange(index, 'phone')}
                                    disabled={readOnly || submitting}
                                />
                                <Tooltip title="Remover contacto de referência">
                                    <span>
                                        <IconButton
                                            onClick={() => onRemoveReferenceContact(index)}
                                            disabled={
                                                readOnly ||
                                                submitting ||
                                                (form.referenceContacts || []).length === 1
                                            }
                                            sx={{ color: '#3D3D3D' }}
                                        >
                                            <RemoveCircleOutlineRoundedIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        ))}
                    </Stack>

                    <Typography variant="h6" fontWeight={700} mb={1}>
                        Situação Psicossocial
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                            gap: 2,
                            mb: 2
                        }}
                    >
                        <TextField
                            label="Há quanto tempo presta cuidados?"
                            fullWidth
                            value={form.careDuration}
                            onChange={onChange('careDuration')}
                            disabled={readOnly || submitting}
                        />
                        <TextField
                            label="Recebe apoio nos cuidados que presta?"
                            fullWidth
                            value={form.hasCareSupport}
                            onChange={onChange('hasCareSupport')}
                            disabled={readOnly || submitting}
                        />
                        <TextField
                            label="Tipo de apoio"
                            fullWidth
                            value={form.supportType}
                            onChange={onChange('supportType')}
                            disabled={readOnly || submitting}
                        />
                    </Box>
                    <Box sx={{ mb: 3 }}>
                        <TextField
                            label="Outras informações importantes acerca da situação psicossocial"
                            fullWidth
                            multiline
                            minRows={4}
                            value={form.psychosocialNotes}
                            onChange={onChange('psychosocialNotes')}
                            disabled={readOnly || submitting}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                            gap: 2,
                            mb: 3
                        }}
                    >
                        <TextField
                            label="Conta Criada"
                            placeholder="dd/mm/aaaa"
                            fullWidth
                            value={form.accountCreated || ''}
                            disabled
                            InputProps={{ readOnly: true }}
                        />
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Ativo desde"
                                format="DD/MM/YYYY"
                                value={toPickerValue(form.activeSince)}
                                onChange={(newValue) =>
                                    onChange('activeSince')({
                                        target: {
                                            value:
                                                newValue && dayjs(newValue).isValid()
                                                    ? dayjs(newValue).format('YYYY-MM-DD')
                                                    : ''
                                        }
                                    })
                                }
                                disabled={readOnly || submitting}
                                slotProps={{
                                    textField: {
                                        fullWidth: true
                                    }
                                }}
                            />
                        </LocalizationProvider>
                        <Box />
                    </Box>

                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                        spacing={2}
                        mb={1}
                    >
                        <Typography variant="h6" fontWeight={700}>
                            Equipamentos
                        </Typography>
                        {!readOnly ? (
                            <Tooltip title="Adicionar equipamento">
                                <span>
                                    <IconButton onClick={onAddEquipmentItem} disabled={submitting} sx={{ color: '#3D3D3D' }}>
                                        <AddCircleOutlineRoundedIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        ) : null}
                    </Stack>

                    <Stack spacing={2} mb={3}>
                        {(form.equipmentItems || []).map((item, index) => (
                            <Box
                                key={`equipment-${index}`}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '220px 1fr auto' },
                                    gap: 2,
                                    alignItems: 'center'
                                }}
                            >
                                <TextField
                                    label="Tipo"
                                    fullWidth
                                    select
                                    value={item.type || 'Tablet'}
                                    onChange={onEquipmentItemChange(index, 'type')}
                                    disabled={readOnly || submitting}
                                >
                                    <MenuItem value="Tablet">Tablet</MenuItem>
                                    <MenuItem value="Wearable">Wearable</MenuItem>
                                    <MenuItem value="Outro">Outro</MenuItem>
                                </TextField>
                                <TextField
                                    label="Nº de Identificação / Observaões"
                                    fullWidth
                                    value={item.details || ''}
                                    onChange={onEquipmentItemChange(index, 'details')}
                                    disabled={readOnly || submitting}
                                />
                                {!readOnly ? (
                                    <Tooltip title="Remover equipamento">
                                        <span>
                                            <IconButton
                                                onClick={() => onRemoveEquipmentItem(index)}
                                                disabled={(form.equipmentItems || []).length === 1 || submitting}
                                                sx={{ color: '#3D3D3D' }}
                                            >
                                                <RemoveCircleOutlineRoundedIcon />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                ) : (
                                    <Box />
                                )}
                            </Box>
                        ))}
                    </Stack>

                    <Stack
                                direction="row"
                                justifyContent="flex-end"
                                alignItems="center"
                                mt={3}
                                mb={2}
                            >
                        {!readOnly ? (
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    onClick={onCancel}
                                    disabled={submitting}
                                    sx={secondaryDialogButtonSx}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    variant="contained"
                                    sx={primaryDialogButtonSx}
                                    onClick={onSave}
                                    disabled={submitting}
                                >
                                    {submitting ? 'A guardar...' : 'Guardar'}
                                </Button>
                            </Stack>
                        ) : null}
                    </Stack>
                </Collapse>
            </Box>

            <Divider sx={{ mb: 3, borderColor: '#7f7f7f' }} />

            <Box
                sx={{
                    p: 3,
                    borderRadius: 2,
                    border: 1,
                    borderColor: '#dbdbdb',
                    bgcolor: '#ffffff'
                }}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    onClick={handlePatientHeaderClick}
                    sx={{
                        cursor:
                            !hasPatient && !showPatientForm
                                ? 'default'
                                : patientExpanded
                                    ? 'default'
                                    : 'pointer'
                    }}
                >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box>
                            <Typography variant="h6" fontWeight={700} mb={hasPatient || showPatientForm ? 2 : 0.5}>
                                Ficha Pessoal: Paciente
                            </Typography>
                            {!hasPatient && !showPatientForm ? (
                                <Typography variant="body2" color="#8A8A8A">
                                    Sem paciente associado
                                </Typography>
                            ) : null}
                        </Box>
                        {patientExpanded && showPatientForm && hasPatient && patientReadOnly ? (
                            <Stack direction="row" spacing={1}>
                                {canDeletePatient ? (
                                    <Tooltip title="Eliminar paciente">
                                        <span>
                                            <Fab
                                                size="small"
                                                sx={{ bgcolor: '#3D3D3D', color: '#fff', boxShadow: 'none' }}
                                                onClick={(event) => {
                                                    stopPropagation(event);
                                                    onDeletePatient();
                                                }}
                                                disabled={deletingPatient}
                                            >
                                                <DeleteOutlineRoundedIcon />
                                            </Fab>
                                        </span>
                                    </Tooltip>
                                ) : null}
                                <Tooltip title="Editar dados do paciente">
                                    <span>
                                        <Fab
                                            size="small"
                                            sx={{ bgcolor: '#D63731', color: '#fff', boxShadow: 'none' }}
                                            onClick={(event) => {
                                                stopPropagation(event);
                                                onPatientEdit();
                                            }}
                                        >
                                            <Box
                                                component="img"
                                                src="/assets/logos-svg/edit.svg"
                                                alt="Editar"
                                                sx={{ width: 18, height: 18, display: 'block' }}
                                            />
                                        </Fab>
                                    </span>
                                </Tooltip>
                            </Stack>
                        ) : null}
                    </Stack>
                    {!hasPatient && !showPatientForm ? (
                        <Tooltip title="Adicionar paciente associado">
                            <Button
                                variant="contained"
                                startIcon={<AddCircleOutlineRoundedIcon />}
                                onClick={(event) => {
                                    stopPropagation(event);
                                    onAddPatientClick();
                                    setPatientExpanded(true);
                                }}
                                sx={primaryDialogButtonSx}
                            >
                                Adicionar
                            </Button>
                        </Tooltip>
                    ) : (
                        <Tooltip title={patientExpanded ? 'Fechar ficha do paciente' : 'Abrir ficha do paciente'}>
                            <IconButton
                                onClick={(event) => {
                                    stopPropagation(event);
                                    setPatientExpanded((prev) => !prev);
                                }}
                            >
                                <ExpandMoreIcon
                                    sx={{
                                        transform: patientExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform .2s ease'
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>

                <Collapse in={patientExpanded}>
                    {showPatientForm ? (
                        <Box sx={createReadOnlyViewFieldSx(hasPatient ? patientReadOnly : false)}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                                    gap: 2,
                                    mb: 2,
                                    mt: 2
                                }}
                            >
                                <TextField
                                    label="Nome Completo"
                                    fullWidth
                                    value={patientForm.name}
                                    onChange={onPatientChange('name')}
                                    disabled={
                                        hasPatient
                                            ? patientReadOnly || patientSubmitting
                                            : patientSubmitting
                                    }
                                />
                                <TextField
                                    label="Género"
                                    fullWidth
                                    select
                                    value={patientForm.gender}
                                    onChange={onPatientChange('gender')}
                                    disabled={
                                        hasPatient
                                            ? patientReadOnly || patientSubmitting
                                            : patientSubmitting
                                    }
                                >
                                    <MenuItem value="">Selecionar</MenuItem>
                                    <MenuItem value="WOMAN">Feminino</MenuItem>
                                    <MenuItem value="MAN">Masculino</MenuItem>
                                    <MenuItem value="NOT_LISTED">Outro</MenuItem>
                                </TextField>
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                    gap: 2,
                                    mb: 2
                                }}
                            >
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Data de Nascimento"
                                        format="DD/MM/YYYY"
                                        value={toPickerValue(patientForm.birthDate)}
                                        onChange={(newValue) =>
                                            onPatientChange('birthDate')({
                                                target: {
                                                    value:
                                                        newValue && dayjs(newValue).isValid()
                                                            ? dayjs(newValue).format('YYYY-MM-DD')
                                                            : ''
                                                }
                                            })
                                        }
                                        disabled={
                                            hasPatient
                                                ? patientReadOnly || patientSubmitting
                                                : patientSubmitting
                                        }
                                        slotProps={{
                                            textField: {
                                                fullWidth: true
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                                <TextField
                                    label="Idade"
                                    fullWidth
                                    value={patientForm.age}
                                    disabled
                                    InputProps={{ readOnly: true }}
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                                    gap: 2,
                                    mb: 2
                                }}
                            >
                                <TextField
                                    label="Morada"
                                    fullWidth
                                    value={patientForm.street}
                                    onChange={onPatientChange('street')}
                                    disabled={
                                        hasPatient
                                            ? patientReadOnly || patientSubmitting
                                            : patientSubmitting
                                    }
                                />
                                <TextField
                                    label="Código Postal"
                                    fullWidth
                                    value={patientForm.zip}
                                    onChange={onPatientChange('zip')}
                                    disabled={
                                        hasPatient
                                            ? patientReadOnly || patientSubmitting
                                            : patientSubmitting
                                    }
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                    gap: 2,
                                    mb: 2
                                }}
                            >
                                <TextField
                                    label="Freguesia"
                                    fullWidth
                                    value={patientForm.parish}
                                    onChange={onPatientChange('parish')}
                                    disabled={
                                        hasPatient
                                            ? patientReadOnly || patientSubmitting
                                            : patientSubmitting
                                    }
                                />
                                <TextField
                                    label="Concelho"
                                    fullWidth
                                    value={patientForm.municipality}
                                    onChange={onPatientChange('municipality')}
                                    disabled={
                                        hasPatient
                                            ? patientReadOnly || patientSubmitting
                                            : patientSubmitting
                                    }
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                                    gap: 2,
                                    mb: 2
                                }}
                            >
                                <TextField
                                    label="N.º BI / CC"
                                    fullWidth
                                    value={patientForm.idCard}
                                    onChange={onPatientChange('idCard')}
                                    disabled={
                                        hasPatient
                                            ? patientReadOnly || patientSubmitting
                                            : patientSubmitting
                                    }
                                />
                                <TextField
                                    label="NIF"
                                    fullWidth
                                    value={patientForm.nif}
                                    onChange={onPatientChange('nif')}
                                    disabled={
                                        hasPatient
                                            ? patientReadOnly || patientSubmitting
                                            : patientSubmitting
                                    }
                                />
                                <TextField
                                    label="N.º Utente"
                                    fullWidth
                                    value={patientForm.healthNumber}
                                    onChange={onPatientChange('healthNumber')}
                                    disabled={
                                        hasPatient
                                            ? patientReadOnly || patientSubmitting
                                            : patientSubmitting
                                    }
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                                    gap: 2,
                                    mb: 2
                                }}
                            >
                                <TextField
                                    label="NISS"
                                    fullWidth
                                    value={patientForm.niss}
                                    onChange={onPatientChange('niss')}
                                    disabled={
                                        hasPatient
                                            ? patientReadOnly || patientSubmitting
                                            : patientSubmitting
                                    }
                                />
                                <Box />
                                <Box />
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                                    gap: 2,
                                    mb: 2
                                }}
                            >
                                <TextField
                                    label="Estado Civil"
                                    fullWidth
                                    value={patientForm.maritalStatus}
                                    onChange={onPatientChange('maritalStatus')}
                                    disabled={
                                        hasPatient
                                            ? patientReadOnly || patientSubmitting
                                            : patientSubmitting
                                    }
                                />
                                <TextField
                                    label="Escolaridade"
                                    fullWidth
                                    value={patientForm.education}
                                    onChange={onPatientChange('education')}
                                    disabled={
                                        hasPatient
                                            ? patientReadOnly || patientSubmitting
                                            : patientSubmitting
                                    }
                                />
                                <TextField
                                    label="Situação Profissional"
                                    fullWidth
                                    value={patientForm.professionalStatus}
                                    onChange={onPatientChange('professionalStatus')}
                                    disabled={
                                        hasPatient
                                            ? patientReadOnly || patientSubmitting
                                            : patientSubmitting
                                    }
                                />
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                                    gap: 2
                                }}
                            >
                                <TextField
                                    label="Incapacidade"
                                    fullWidth
                                    value={patientForm.disability}
                                    onChange={onPatientChange('disability')}
                                    disabled={
                                        hasPatient
                                            ? patientReadOnly || patientSubmitting
                                            : patientSubmitting
                                    }
                                />
                                <Box />
                            </Box>
                            <Stack
                                direction="row"
                                justifyContent="flex-end"
                                alignItems="center"
                                mt={3}
                                mb={2}
                            >
                                {!showPatientForm ? (
                                    <Button
                                        variant="contained"
                                        sx={{ bgcolor: '#3f3f3f' }}
                                        onClick={onAddPatientClick}
                                    >
                                        Adicionar Pessoa a Cuidados
                                    </Button>
                                ) : !patientReadOnly || !hasPatient ? (
                                    <Stack direction="row" spacing={1}>
                                        <Button
                                            variant="outlined"
                                            onClick={onPatientCancel}
                                            disabled={patientSubmitting}
                                            sx={secondaryDialogButtonSx}
                                        >
                                            Cancelar
                                        </Button>
                                        <Button
                                            variant="contained"
                                            sx={primaryDialogButtonSx}
                                            onClick={onPatientSave}
                                            disabled={patientSubmitting}
                                        >
                                            {patientSubmitting ? 'A guardar...' : 'Guardar'}
                                        </Button>
                                    </Stack>
                                ) : null}
                            </Stack>
                        </Box>
                    ) : null}
                </Collapse>
            </Box>
        </Box>
    );
};

export default CaregiverPersonalDataSection;



